import { Check, Loader2, Zap, AlertTriangle, Lock, Pencil } from 'lucide-react'
import { getDisplayName, getInformalName } from '@/lib/teams'

interface SubmitBarProps {
  selectedTeam: string | null
  tddEnabled: boolean
  isSubmitting: boolean
  pickSaved: boolean
  hasExistingPick: boolean
  existingPickTeam: string | null
  autopickTeam?: string | null
  countdown: {
    formatted: string
    hasDeadline: boolean
    isExpired: boolean
  }
  onSubmit: () => void
  onChangePick: () => void
}

export function SubmitBar({
  selectedTeam,
  tddEnabled,
  isSubmitting,
  pickSaved,
  hasExistingPick,
  existingPickTeam,
  autopickTeam,
  countdown,
  onSubmit,
  onChangePick,
}: SubmitBarProps) {
  const canSubmit = selectedTeam !== null

  return (
    <div className="fixed bottom-[calc(3.5rem+env(safe-area-inset-bottom))] md:bottom-0 left-0 right-0 z-40 bg-white dark:bg-zinc-900 border-t border-zinc-200 dark:border-zinc-800 p-4">
      <div className="max-w-lg mx-auto">
        {/* State 1: Pick is saved — finalized look with "Edit Pick" */}
        {pickSaved && selectedTeam ? (
          <div className="flex items-center gap-3">
            <div className="flex-1">
              <div className="flex items-center gap-1.5">
                <Lock className="w-3.5 h-3.5 text-violet-500 shrink-0" />
                <p className="text-sm font-medium text-violet-600 dark:text-violet-400">Your Pick</p>
              </div>
              <p className="font-semibold text-zinc-900 dark:text-zinc-100 mt-0.5">
                {getDisplayName(selectedTeam)}
                {tddEnabled && (
                  <span className="ml-2 inline-flex items-center gap-1 text-xs font-bold text-violet-600 dark:text-violet-400">
                    <Zap className="w-3 h-3" /> DD
                  </span>
                )}
              </p>
            </div>
            <button
              onClick={onChangePick}
              className="flex items-center gap-2 px-5 py-2.5 bg-violet-100 hover:bg-violet-200 dark:bg-violet-900/40 dark:hover:bg-violet-900/60 text-violet-700 dark:text-violet-300 font-medium rounded-xl transition-colors"
            >
              <Pencil className="w-4 h-4" />
              Edit Pick
            </button>
          </div>
        ) : canSubmit ? (
          /* State 2: Team selected but not yet saved — show "Submit Pick" */
          <div className="space-y-2">
            <div className="flex items-center gap-3">
              <div className="flex-1">
                <p className="text-sm text-zinc-600 dark:text-zinc-400">Your pick:</p>
                <p className="font-semibold text-zinc-900 dark:text-zinc-100">
                  {getDisplayName(selectedTeam)}
                  {tddEnabled && (
                    <span className="ml-2 inline-flex items-center gap-1 text-xs font-bold text-violet-600 dark:text-violet-400">
                      <Zap className="w-3 h-3" /> DD
                    </span>
                  )}
                </p>
              </div>
              <button
                onClick={onSubmit}
                disabled={isSubmitting}
                className="flex items-center gap-2 px-6 py-3 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white font-semibold rounded-xl transition-colors"
              >
                {isSubmitting ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <Check className="w-5 h-5" />
                )}
                {isSubmitting ? 'Submitting...' : 'Submit Pick'}
              </button>
            </div>
            {autopickTeam && countdown.hasDeadline && !countdown.isExpired && !hasExistingPick && (
              <p className="text-xs text-amber-600 dark:text-amber-400 flex items-center justify-center gap-1">
                <AlertTriangle className="w-3 h-3" />
                Autopick: <span className="font-semibold">{getInformalName(autopickTeam)}</span> if no pick submitted
              </p>
            )}
          </div>
        ) : (
          /* State 3: No team selected — prompt to select */
          <div className="text-center space-y-1">
            {hasExistingPick && existingPickTeam ? (
              <>
                <p className="text-sm text-zinc-900 dark:text-zinc-100">
                  Current pick: <span className="font-semibold">{getDisplayName(existingPickTeam)}</span>
                  {countdown.hasDeadline && !countdown.isExpired && (
                    <> · <span className="font-mono text-emerald-600 dark:text-emerald-400">{countdown.formatted}</span></>
                  )}
                </p>
                <p className="text-xs text-zinc-500 dark:text-zinc-400">
                  Select a new team below or keep your current pick
                </p>
              </>
            ) : (
              <>
                <p className="text-sm font-medium text-zinc-900 dark:text-zinc-100">
                  Select a team
                  {countdown.hasDeadline && !countdown.isExpired && (
                    <> · <span className="font-mono text-emerald-600 dark:text-emerald-400">{countdown.formatted}</span></>
                  )}
                </p>
                {autopickTeam && countdown.hasDeadline && !countdown.isExpired && (
                  <p className="text-xs text-amber-600 dark:text-amber-400 flex items-center justify-center gap-1">
                    <AlertTriangle className="w-3 h-3" />
                    Autopick: <span className="font-semibold">{getInformalName(autopickTeam)}</span> if no pick submitted
                  </p>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
